<template>
  <div class="profile-info-page" :class="showCollapse && 'collapse'">
    <div class="profile-info">
      <div v-if="!loading" class="profile-info-container">
        <div v-if="!hiddenBreadcrumb" class="breadcrumb-navigation">
          <span class="navigation" @click="handleBreadCrumbNavigationClick">{{ $t(`admin.layout.newLayout.menu.applicantList`) }}</span>
          <span class="split">/</span>
          <span class="navigation active">{{ $t(`admin.layout.newLayout.menu.applicantProfile`) }}</span>
        </div>
        <div v-if="pageFrom !== 'duplicate' && !candidateInfo.is_revoked && !hiddenBreadcrumb" class="application-list-navigation">
          <el-button class="navigation-btn prev-btn" :disabled="currentApplicationIdIndex === 0 || aiResumeMatchLoading || applicationChanging" @click="handlePrevBtnClick">
            <i class="btn-icon el-icon-arrow-left"></i>
          </el-button>
          <span class="position">
            <span class="current">{{ (currentApplicationIdIndex + 1)|formatBigNumber }}</span>
            <span class="split">/</span>
            <span class="total">{{ totalCount|formatBigNumber }}</span>
          </span>
          <el-button class="navigation-btn next-btn" :disabled="(currentApplicationIdIndex + 1) === totalCount || aiResumeMatchLoading || applicationChanging" @click="handleNextBtnClick">
            <i class="btn-icon el-icon-arrow-right"></i>
          </el-button>
          <RectLoading v-show="applicationIdListLoading" class="loading" ref="loading"></RectLoading>
        </div>
        <div class="base-info-container" :class="(candidateInfo.is_employee || candidateInfo.is_alumni) && 'extra'">
          <BaseInfo
            :candidateInfo="candidateInfo"
            :statusGroupList="statusGroupList"
            :isDisabled="isDisabled"
            @toRejectStatus="handleToRejectStatus"
            @changeStatus="handleChangeStatus"
          />
        </div>
        <div class="detail-info-container" ref="detailInfoContainer">
          <TabCard
            class="info-tab-card"
            :activeTab="activeTab"
            :tabList="tabList"
            @change="onTabChange"
          >
            <component
              v-bind="activeTab.props"
              v-on="activeTab.events"
              :is="activeTab.component"
              :candidateBaseInfo="candidateInfo"
              :aiResumeMatchLoading="aiResumeMatchLoading"
              :ref="activeTab.ref"
              :isDisabled="isDisabled"
            ></component>
            <template #resumeScore>
              <div v-if="resumeAIMatchSummary" class="score-container">
                <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="resumeAIMatchSummary">
                  <div ref="resumeFit" class="chart-score resume-fit"></div>
                </el-tooltip>
              </div>
            </template>
            <template #ratingScore>
              <div v-if="showRateChat" class="score-container">
                <RecommendationResultChart
                  class="chart-score"
                  ringBackgroundColor="#dcdfe6"
                  :score="resumeRating.total_rate"
                  :decision="resumeRating.rating_decision"
                  :completion="resumeRating.completion"
                  :hmInfoList="resumeRating.hire_manager_list"
                  :fitScoreStyle="tabScoreStyle"
                  :circleWidth="2"
                  :fontSize="14"
                />
              </div>
            </template>
            <template #assessmentScore>
              <div class="score-container">
                <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.resumeFit`)">
                  <div ref="assessmentTotalFit" class="chart-score total-fit"></div>
                </el-tooltip>
                <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.problemSolvingFit`)">
                  <div ref="assessmentMentalFit" class="chart-score mental-fit"></div>
                </el-tooltip>
              </div>
            </template>
            <template #interviewScore>
              <div v-if="showInterviewRateChat" class="score-container">
                <RecommendationResultChart
                  class="chart-score"
                  ringBackgroundColor="#dcdfe6"
                  :score="interviewRating.total_rate"
                  :decision="interviewRating.interview_decision"
                  :completion="interviewRating.completion"
                  :hmInfoList="interviewRating.hire_manager_list"
                  :fitScoreStyle="tabScoreStyle"
                  :circleWidth="2"
                  :fontSize="14"
                />
              </div>
            </template>
            <template #appliedJobsCount>
              <div v-if="showAppliedJobsCount" class="score-container">
                <el-tooltip
                  effect="light"
                  popper-class="light-tooltip-popper applied-jobs-tooltip"
                >
                  <template #content>
                    <div class="applied-jobs-tooltip-content">
                      <div class="tooltip-line total-line">{{ $t(`${i18nPrefix}.label.appliedJobsTotalLabel`) }}{{ appliedJobsCount }}</div>
                      <template v-if="recentAppliedJobs.length > 0">
                        <div class="tooltip-line section-title">{{ $t(`${i18nPrefix}.label.otherAppliedJobsLabel`) }}</div>
                        <div
                          v-for="(item, index) in recentAppliedJobs"
                          :key="`${item.job && item.job.id ? item.job.id : 'job'}_${index}`"
                          class="tooltip-job-item"
                        >
                          <span class="dot"></span>
                          <span v-auto-tooltip="{ content: item.job && item.job.i18nName }" class="title">{{ item.job && item.job.i18nName }}</span>
                        </div>
                      </template>
                    </div>
                  </template>
                  <div class="score-item applied-jobs-score-item" :class="{ 'decimal-score-item': hasDecimalPoint(appliedJobsCount) }">{{ appliedJobsCount }}</div>
                </el-tooltip>
              </div>
            </template>
            <template #npsScore>
              <div v-if="hasSatisfactionSurveyCandidate && candidateInfo.nps_score" class="score-container">
                <div class="score-item" :class="{ 'decimal-score-item': hasDecimalPoint(candidateInfo.nps_score) }">{{ candidateInfo.nps_score }}</div>
              </div>
            </template>
            <template #offerLetterStatus>
              <div v-if="candidateInfo.offerLetterStatusColor" class="offer-status" :style="{'--offer-status-color': candidateInfo.offerLetterStatusColor}"></div>
            </template>
          </TabCard>
        </div>
        <ChangeCandidateStatus
          ref="changeStatusDialog"
          :loadedStatusList="statusList"
          :defaultStatus="defaultStatusKey"
          :applicationList="applicationList"
          @success="handleChangeStatusSuccess"
        />
        <div v-if="pageFrom !== 'duplicate' && !candidateInfo.is_revoked && !hiddenBreadcrumb" class="fixed-list-navigation" :style="fixedNavigationStyle">
          <el-button class="navigation-btn prev-btn" :disabled="currentApplicationIdIndex === 0 || aiResumeMatchLoading || applicationChanging" @click="handlePrevBtnClick">
            <i class="btn-icon iconfont iconArrows"></i>
          </el-button>
          <el-button class="navigation-btn next-btn" :disabled="(currentApplicationIdIndex + 1) === totalCount || aiResumeMatchLoading || applicationChanging" @click="handleNextBtnClick">
            <i class="btn-icon iconfont iconArrows"></i>
          </el-button>
        </div>
      </div>
      <CollapseInfo
        v-if="!loading && (!candidateInfo.is_agency_uploaded_and_hid || isAcceptAgencyUploadedCandidate)"
        ref="collapseInfo"
        class="collapse-container"
        showInterviewerCanView
        :isMyComment="isMyComment"
        :candidateBaseInfo="candidateInfo"
        :isDisabled="isDisabled"
        @updateHrQuestion="updateHrQuestion"
        @open="handleOpen"
        @close="handleClose"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import moment from 'moment'
import { mapActions } from 'vuex'
import { getI18nPropertyValue, addI18nProperty, createI18nProperty } from '@/i18n'
import { generateFitEchartsOption } from '@/constant/application'
import BaseInfo from './components/baseInfo.vue'
import TabCard from './components/tabCard.vue'
import Resume from './resume.vue'
import OpenQuestion from './openQuestion.vue'
import Assessment from './assessment.vue'
import Onboarding from './onboarding.vue'
import Interview from './interview.vue'
import ActivityTracker from './activityTracker.vue'
import Tag from './tag.vue'
import SatisfactionSurvey from './satisfactionSurvey.vue'
import PostApplicant from './postApplicant'
import JobApplication from './jobApplication.vue'
import Offer from './offer.vue'
import ChangeCandidateStatus from '../../applicantList/dialog/changeCandidateStatus.vue'
import RectLoading from '@/components/loading/rectLoading.vue'
import CollapseInfo from './components/collapseInfo.vue'
import RecommendationResultChart from '@/views/adminNew/components/recommendationResultChart.vue'
import { ROOT_COMPANY_ID } from '@/constant/company'
import { CANDIDATE_OFFER_LETTER_STATUS_COLOR_MAP } from '@/constant/candidate'
import { AsyncTask, getJobFitScoreColor, formatJobFitScore } from '@/utils/common'

const PAGE_TYPE = {
  RESUME: 'resume',
  OPEN_QUESTION: 'screening',
  ASSESSMENT: 'assessment',
  INTERVIEW: 'interview',
  ONBOARDING: 'onboarding',
  ACTIVITY_TRACKER: 'activity-tracker',
  TAG: 'tag',
  SATISFACTION: 'satisfaction',
  PORTAL: 'portal',
  OFFER: 'offer',
  JOB_APPLICATION: 'job-application'
}

const AGENCY_UPLOADED_CANDIDATE_STATUS = {
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
  PENDING: 'PENDING'
}

export default {
  beforeRouteLeave (to, from, next) {
    let canLeave = true
    if (this.$refs.collapseInfo) {
      canLeave = this.$refs.collapseInfo?.checkRouteCanLeave()
    }
    return canLeave ? next() : false
  },
  components: {
    BaseInfo,
    TabCard,
    Resume,
    JobApplication,
    ChangeCandidateStatus,
    RectLoading,
    CollapseInfo,
    RecommendationResultChart
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      activeTab: null,
      pageDataLoader: null,
      candidateInfo: {},
      statusGroupList: [],
      statusList: [],
      defaultStatusKey: null,
      applicationIdList: [],
      applicationIdListLoading: false,
      applicationChanging: false,
      fixedNavigationRightResetter: null,
      fixedNavigationRight: 110,
      total_avg_interview_rate: '',
      latest_interview_rate: null,
      total_avg_rate: null,
      latest_rate: null,
      resumeRating: null,
      interviewRating: null,
      tabScoreStyle: {
        width: '32px',
        height: '32px',
        backgroundColor: '#fff'
      },
      aiResumeMatchLoading: false,
      showCollapse: false,
      hasHrQuestion: false,
      offerLetterStatusColor: null,
      isMyComment: false,
      activityTrackerData: null,
      candidateAppliedJobs: [],
      appliedJobsLoaded: false
    }
  },
  computed: {
    fixedNavigationStyle () {
      return {
        right: `${this.fixedNavigationRight}px`
      }
    },
    formatTotalAvgRate () {
      return this.formatStringFloorNumber(this.total_avg_rate)
    },
    formatTotalAvgInterviewRate () {
      return this.formatStringFloorNumber(this.total_avg_interview_rate)
    },
    showRateChat () {
      return Boolean(this.resumeRating?.rating_decision)
    },
    showInterviewRateChat () {
      return Boolean(this.interviewRating?.interview_decision)
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    hasAssessmentPermission () {
      return this.hasAnyPermission('PREMIUM_ASSESSMENTS')
    },
    hasSatisfactionSurveyCandidate () {
      return this.hasAnyPermission(['PREMIUM_SATISFACTION_SURVEY_CANDIDATE', 'PERM_SATISFACTION_SURVEY_MANAGE'])
    },
    hasOnlyActionOnMyCandidatePermission () {
      return this.hasAnyPermission('PERM_ONLY_ACTION_ON_MY_CANDIDATE')
    },
    appliedJobsCount () {
      return this.candidateAppliedJobs.length
    },
    recentAppliedJobs () {
      return _
        .chain(this.candidateAppliedJobs)
        .orderBy(item => moment(item.apply_time).valueOf() || 0, ['desc'])
        .take(2)
        .value()
    },
    showAppliedJobsCount () {
      return this.appliedJobsLoaded && this.appliedJobsCount > 0
    },
    isDisabled () {
      return this.hasOnlyActionOnMyCandidatePermission && !this.candidateInfo?.job?.is_my_job
    },
    tabList () {
      const vm = this
      const tabList = [
        {
          pageType: PAGE_TYPE.RESUME,
          component: Resume,
          suffix: 'resumeScore',
          props: {
            get statusList () { return vm.statusList },
            showInterviewerCanView: true
          },
          events: {
            createAIMatch: vm.handleCreateAIResumeMatchBtnClick
          }
        },
        {
          pageType: PAGE_TYPE.OPEN_QUESTION,
          component: OpenQuestion,
          suffix: 'ratingScore',
          props: {
            get hasHrQuestion () { return vm.hasHrQuestion }
          },
          events: {
            refreshProfileTab: vm.loadApplicationCandidateProfileTabInfo
          }
        },
        {
          pageType: PAGE_TYPE.ASSESSMENT,
          component: Assessment,
          suffix: 'assessmentScore',
          permission: ['PREMIUM_ASSESSMENTS']
        },
        {
          pageType: PAGE_TYPE.INTERVIEW,
          component: Interview,
          suffix: 'interviewScore',
          permission: ['PREMIUM_INTERVIEW_SCHEDULING'],
          events: {
            refreshProfileTab: vm.loadApplicationCandidateProfileTabInfo
          }
        },
        {
          pageType: PAGE_TYPE.OFFER,
          component: Offer,
          suffix: 'offerLetterStatus',
          permission: ['PREMIUM_OFFER_MANAGEMENT', 'PERM_OFFER_INITIATE'],
          events: {
            updateOfferLetterStatusColor: vm.handleUpdateOfferLetterStatusColor
          }
        },
        {
          pageType: PAGE_TYPE.ONBOARDING,
          component: Onboarding,
          permission: ['PREMIUM_ONBOARDING']
        },
        {
          pageType: PAGE_TYPE.ACTIVITY_TRACKER,
          props: {
            isAcceptAgencyUploadedCandidate: this.isAcceptAgencyUploadedCandidate,
            showInterviewerCanView: true,
            get prefetchedActivityData () { return vm.activityTrackerData }
          },
          component: ActivityTracker,
          suffix: 'appliedJobsCount',
          ref: 'activityTracker'
        },
        {
          pageType: PAGE_TYPE.TAG,
          component: Tag,
          permission: { strict: false, any: ['PREMIUM_TAG_MANAGEMENT', 'PREMIUM_CRM_PARSE_RESUME_SDK_TAG'] }
        },
        {
          pageType: PAGE_TYPE.PORTAL,
          component: PostApplicant,
          permission: ['PREMIUM_POST_APPLICANT']
        },
        {
          pageType: PAGE_TYPE.JOB_APPLICATION,
          component: JobApplication,
          permission: ['PREMIUM_APPLICATION_FORM']
        },
        {
          pageType: PAGE_TYPE.SATISFACTION,
          component: SatisfactionSurvey,
          suffix: 'npsScore',
          permission: ['PREMIUM_SATISFACTION_SURVEY_CANDIDATE']
        }
      ]
      const hidePageType = []
      if (this.candidateInfo?.is_agency_uploaded_and_hid) {
        hidePageType.push(PAGE_TYPE.TAG)
      }
      return _.chain(tabList).filter(tab => {
        if (hidePageType.includes(tab.pageType)) {
          return false
        }
        if (tab.permission) {
          return this.hasAnyPermission(tab.permission)
        }
        return true
      }).map(tab => {
        const pageTypeKey = _.findKey(PAGE_TYPE, pageType => pageType === tab.pageType)
        tab.labelI18nKey = `${this.i18nPrefix}.tabMenu.${pageTypeKey}`
        return tab
      }).value()
    },
    pageType () {
      return this.$route.params.pageType
    },
    candidateId () {
      return this.$route.query.candidate_id
    },
    jobId () {
      return this.$route.query.job_id
    },
    profileTabRequestKey () {
      return `${this.jobId}_${this.candidateId}`
    },
    listPageParams () {
      return this.$route.query.list_params
    },
    pageFrom () {
      return this.$route.query.page_from
    },
    pageTitle () {
      const title = this.$t(`${this.i18nPrefix}.title.detailResume`)
      const tabLabel = this.activeTab && this.$t(this.activeTab.labelI18nKey)
      const candidateName = this.candidateInfo?.other_name
      return _.chain([candidateName, title, tabLabel]).filter().join(' - ').value()
    },
    applicationList () {
      return [{
        id: this.candidateInfo.application_id,
        job: {
          ...this.candidateInfo.job,
          company: this.candidateInfo.company
        },
        candidate: this.candidateInfo.id
      }]
    },
    totalCount () {
      return this.applicationIdList?.length
    },
    currentApplicationIdIndex () {
      return _.findIndex(this.applicationIdList, { id: this.candidateInfo.application_id })
    },
    hiddenBreadcrumb () {
      return this.$route.query.hidden_bread_crumb
    },
    resumeAIMatchSummary () {
      if (!this.hasAIResumeMatchPermission) {
        return ''
      }
      return getI18nPropertyValue(this.candidateInfo, 'resume_ai_match_summary_cn', 'resume_ai_match_summary_en')
    },
    isAcceptAgencyUploadedCandidate () {
      return this.candidateInfo.agency_uploaded_candidate_status === AGENCY_UPLOADED_CANDIDATE_STATUS.ACCEPTED
    }
  },
  created () {
    this.$eventBus.$on('reloadCandidateResumeBaseInfo', (callback) => this.loadApplicationCandidateBaseInfo().then(callback))
    this.$eventBus.$on('showCollapseInfo', (activeNameList) => this.showCollapseInfo(activeNameList))
    this.$eventBus.$on('changeIsMyCommentsStatus', (status) => {
      this.isMyComment = status
    })
    this.$eventBus.$on('onCommentAdded', (comment, callback) => this.onCommentAdded(comment, callback))
    this.initProfile()
  },
  mounted () {
    this.pageDataLoader.then(() => {
      this.initTabScore()
      this.$nextTick(() => {
        this.resetFixedNavigationRight()
      })
    })
    this.fixedNavigationRightResetter = _.debounce(this.resetFixedNavigationRight, 10)
    window.addEventListener('resize', this.fixedNavigationRightResetter)
    window.onbeforeunload = this.handleWindowBeforeUnload
  },
  destroyed () {
    window.removeEventListener('resize', this.fixedNavigationRightResetter)
  },
  methods: {
    ...mapActions('application', [
      'getApplicationCandidateBaseInfo',
      'getCandidateStatusGroupList',
      'viewApplication',
      'getApplicationIdList',
      'getApplicationCandidateHmRateInfo',
      'getApplicationCandidateInterviewInfo',
      'getApplicationCandidateProfileTabInfo',
      'getApplicationCandidateOfferLetterInfo',
      'getApplicationCandidateActivityInfo'
    ]),
    ...mapActions('ai', ['createAIResumeMatch', 'getAIMatchInfoByTaskId']),
    handleWindowBeforeUnload (e) {
      if (this.$refs.collapseInfo?.hasChangeData) {
        e.preventDefault()
        return ''
      } else {
        window.onbeforeunload = null
      }
    },
    formatStringFloorNumber (str) {
      return _.round(parseFloat(str), 1)
    },
    hasDecimalPoint (value) {
      return String(value).includes('.')
    },
    normalizeAppliedJobInfo (appliedJobs) {
      return _.chain(appliedJobs).map(item => {
        addI18nProperty(item.job, 'i18nName', 'cn_name', 'name')
        addI18nProperty(item.company, 'i18nName', 'cn_name', 'name')
        addI18nProperty(item.status, 'i18nName', 'name_cn', 'name_en')
        return item
      }).value()
    },
    initProfile () {
      this.loadApplicationCandidateInterviewInfo()
      this.loadApplicationCandidateHmRateInfo()
      this.loadApplicationCandidateProfileTabInfo()
      this.loadCandidateOfferLetterInfo()
      this.initPageData()
      this.refreshActiveTab()
      this.loadApplicationIdList()
    },
    resetFixedNavigationRight () {
      const rect = this.$refs.detailInfoContainer.getBoundingClientRect()
      const right = window.innerWidth - rect.left - rect.width + 68
      this.fixedNavigationRight = _.max([right, 104])
    },
    refreshActiveTab () {
      if (!this.pageType) {
        const pageType = this.tabList[0].pageType
        this.$router.push({ path: `/v-admin/application/profile/${pageType}`, query: this.$route.query })
        return
      }
      if (this.activeTab && this.activeTab.pageType === this.pageType) {
        return
      }
      const tab = _.find(this.tabList, { pageType: this.pageType })
      if (!tab) {
        const pageType = this.tabList[0].pageType
        this.$router.replace({ path: `/v-admin/application/profile/${pageType}`, query: this.$route.query })
      } else if (tab.permission && !this.hasAnyPermission(tab.permission)) {
        this.$router.replace('/error/no-permission')
      } else {
        this.activeTab = tab
        this.initTabScore()
      }
    },
    async loadStatusGroupList () {
      const resp = await this.getCandidateStatusGroupList({
        job_id: this.jobId
      })
      if (resp) {
        const statusList = []
        const statusKeyList = []
        _.forEach(resp.status_list, status => {
          statusList.push(addI18nProperty(status, 'i18nName', 'name_cn', 'name_en'))
          statusKeyList.push(status.key)
        })
        this.statusGroupList = _.map(resp.group_list, group => {
          createI18nProperty(group, 'i18nName', `${this.i18nPrefix}.STATUS_GROUP.${group.name}`)
          _.forEach(group.status_list, status => {
            addI18nProperty(status, 'i18nName', 'name_cn', 'name_en')
            status.canChangeTo = statusKeyList.includes(status.key)
          })
          return group
        })
        this.statusList = statusList
      }
    },
    async loadApplicationCandidateBaseInfo () {
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      const resp = await this.getApplicationCandidateBaseInfo(params)
      if (resp) {
        const mobile = resp.mobile || resp.second_phone
        const countryCode = resp.country_code
        resp.mobileWithCountryCode = _.chain([countryCode, mobile]).filter().join(' ').value()
        addI18nProperty(resp.job, 'i18nName', 'cn_title', 'title')
        addI18nProperty(resp.company, 'i18nName', 'cn_name', 'name')
        const age = moment().diff(resp.birthday, 'year')
        createI18nProperty(resp, 'yearsOld', `${this.i18nPrefix}.label.yearsOld`, { age })
        const profile = resp
        profile.total_avg_interview_rate = this.total_avg_interview_rate
        profile.interview_rate_list = this.interview_rate_list
        profile.interview_questions = this.interview_questions
        profile.total_avg_rate = this.total_avg_rate
        profile.rate_list = this.rate_list
        profile.offerLetterStatusColor = this.offerLetterStatusColor
        this.candidateInfo = profile
      }
    },
    async loadApplicationCandidateInterviewInfo () {
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      const resp = await this.getApplicationCandidateInterviewInfo(params)
      if (resp) {
        this.total_avg_interview_rate = resp.total_avg_interview_rate
        this.latest_interview_rate = resp.latest_interview_rate
        this.interview_rate_list = resp.interview_rate_list
        this.interview_questions = resp.interview_questions
        this.$set(this.candidateInfo, 'total_avg_interview_rate', this.total_avg_interview_rate)
        this.$set(this.candidateInfo, 'latest_interview_rate', this.latest_interview_rate)
        this.$set(this.candidateInfo, 'interview_rate_list', this.interview_rate_list)
        this.$set(this.candidateInfo, 'interview_questions', this.interview_questions)
        this.initTabScore()
      }
    },
    async loadApplicationCandidateHmRateInfo () {
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      const resp = await this.getApplicationCandidateHmRateInfo(params)
      if (resp) {
        this.total_avg_rate = resp.total_avg_rate
        this.latest_rate = resp.latest_rate
        this.rate_list = resp.rate_list
        this.$set(this.candidateInfo, 'total_avg_rate', this.total_avg_rate)
        this.$set(this.candidateInfo, 'latest_rate', this.latest_rate)
        this.$set(this.candidateInfo, 'rate_list', this.rate_list)
        this.initTabScore()
      }
    },
    async loadApplicationCandidateProfileTabInfo () {
      this.resumeRating = null
      this.interviewRating = null
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      const resp = await this.getApplicationCandidateProfileTabInfo(params)
      if (resp) {
        this.resumeRating = resp.resume_rating
        this.interviewRating = resp.interview_rating
      }
    },
    async loadApplicationIdList () {
      if (this.pageFrom === 'duplicate') {
        return
      }
      const params = {
        list_params: this.listPageParams,
        job_id: this.jobId,
        page_from: this.$route.query.page_from
      }
      this.applicationIdListLoading = true
      try {
        const resp = await this.getApplicationIdList(params)
        if (resp) {
          this.applicationIdList = resp.application_id_list
        }
      } finally {
        this.applicationIdListLoading = false
      }
    },
    async loadCandidateOfferLetterInfo () {
      const params = {
        job_id: this.jobId,
        candidate_id: this.candidateId
      }
      const resp = await this.getApplicationCandidateOfferLetterInfo(params)
      if (resp) {
        this.setCandidateOfferLetterStatusColor(resp.status)
      }
    },
    async loadActivityTrackerSummary () {
      const params = {
        job_id: this.jobId,
        candidate_id: this.candidateId
      }
      const resp = await this.getApplicationCandidateActivityInfo(params)
      if (resp) {
        const candidateAppliedJobs = this.normalizeAppliedJobInfo(resp.candidate_applied_jobs)
        this.candidateAppliedJobs = candidateAppliedJobs
        this.activityTrackerData = {
          ...resp,
          candidate_applied_jobs: candidateAppliedJobs
        }
      } else {
        this.candidateAppliedJobs = []
        this.activityTrackerData = null
      }
      this.appliedJobsLoaded = true
    },
    async setApplicationViewed () {
      await this.viewApplication({
        candidateId: this.candidateId,
        jobId: this.jobId
      })
    },
    async initTabScore () {
      if (!this.activeTab || !this.candidateInfo) {
        return
      }
      await this.$nextTick()
      // const fitness = this.candidateInfo.fitness
      const fitMap = {}
      const hideRefNames = []
      // 使用 resume_ai_match_score 替换
      const resumeFit = this.candidateInfo.resume_ai_match_score
      if (this.hasAIResumeMatchPermission && resumeFit && resumeFit !== null) {
        fitMap.resumeFit = resumeFit
      } else {
        hideRefNames.push('resumeFit')
      }
      if (this.hasAssessmentPermission) {
        const hasFinishedPersonality = this.candidateInfo.personality_finished
        const hasFinishedProblemSolving = this.candidateInfo.problem_solving_finished
        if (hasFinishedPersonality) {
          fitMap.assessmentTotalFit = parseInt(this.candidateInfo.total_fit * 100) || 0
        } else {
          hideRefNames.push('assessmentTotalFit')
        }
        if (hasFinishedProblemSolving) {
          fitMap.assessmentMentalFit = parseInt(this.candidateInfo.mental_ability * 100) || 0
        } else {
          hideRefNames.push('assessmentMentalFit')
        }
      }
      _.forEach(fitMap, (val, refName) => {
        const refs = this.$refs[refName]
        if (!refs) {
          return
        }
        let el = null
        if (refs instanceof HTMLElement) {
          el = refs
        } else {
          el = refs[0]
        }
        let color = 'rgb(226, 118, 118)' // red
        let displayValue = 0
        const fontSize = 14
        if (refName === 'resumeFit') {
          color = getJobFitScoreColor(val)
          displayValue = formatJobFitScore(val)
        } else {
          displayValue = val
          if (val > 40 && val < 70) {
            color = 'rgb(244, 169, 88)' // orange
          } else if (val >= 70) {
            color = 'rgb(81, 191, 130)' // green
          }
        }
        const option = generateFitEchartsOption(val, displayValue, color, fontSize)
        const series = option.series
        series.axisLine.lineStyle.width = 2
        let chart = echarts.getInstanceByDom(el)
        if (chart) {
          chart.resize()
        } else {
          chart = echarts.init(el)
        }
        chart.setOption(option)
      })
      _.forEach(hideRefNames, async (refName) => {
        let refs = this.$refs[refName]
        if (!refs) {
          await this.$nextTick()
          refs = this.$refs[refName]
        }
        let el = null
        if (refs instanceof HTMLElement) {
          el = refs
        } else if (refs) {
          el = refs[0]
        }
        if (el) {
          el.style.display = 'none'
        }
      })
    },
    async initPageData () {
      this.$eventBus.$emit('showLoading', true)
      this.loading = true
      this.appliedJobsLoaded = false
      this.candidateAppliedJobs = []
      this.activityTrackerData = null
      try {
        this.setApplicationViewed()
        this.pageDataLoader = Promise.all([this.loadApplicationCandidateBaseInfo(), this.loadStatusGroupList(), this.loadActivityTrackerSummary()])
        await this.pageDataLoader
      } finally {
        this.loading = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleApplicationChange () {
      try {
        await Promise.all([
          this.initPageData(),
          this.loadApplicationCandidateInterviewInfo(),
          this.loadApplicationCandidateHmRateInfo(),
          this.loadApplicationCandidateProfileTabInfo(),
          this.loadCandidateOfferLetterInfo()
        ])
        this.refreshActiveTab()
        this.initTabScore()
        this.showCollapse = false
        await this.$nextTick()
        this.resetFixedNavigationRight()
      } finally {
        this.applicationChanging = false
      }
    },
    onCommentAdded (comment, callback) {
      const activityTracker = this.$refs.activityTracker
      const collapseInfo = this.$refs.collapseInfo
      if (activityTracker) {
        activityTracker.onCommentAdded(comment)
      }
      if (collapseInfo) {
        collapseInfo.onCommentAdded(comment)
      }
      if (callback) {
        callback()
      }
    },
    showCollapseInfo (activeNameList) {
      const collapseInfo = this.$refs.collapseInfo
      if (collapseInfo) {
        collapseInfo.handelChangeShowCollapse(activeNameList)
      }
    },
    handleUpdateOfferLetterStatusColor (status) {
      this.setCandidateOfferLetterStatusColor(status)
    },
    setCandidateOfferLetterStatusColor (status) {
      this.offerLetterStatusColor = _.get(CANDIDATE_OFFER_LETTER_STATUS_COLOR_MAP, status, '')
      this.$set(this.candidateInfo, 'offerLetterStatusColor', this.offerLetterStatusColor)
    },
    updateApplicationStatus (callback) {
      const activityTracker = this.$refs.activityTracker
      const collapseInfo = this.$refs.collapseInfo
      if (activityTracker) {
        activityTracker.loadApplicationCandidateActivityInfo()
      }
      if (collapseInfo) {
        collapseInfo.loadActivity()
      }
      if (callback) {
        callback()
      }
    },
    onTabChange (tab) {
      const pageType = tab.pageType
      const path = `/v-admin/application/profile/${pageType}`
      if (this.$route.path === path) {
        return
      }
      this.$router.push({ path, query: this.$route.query })
    },
    handleToRejectStatus () {
      if (this.candidateInfo?.company?.root_company_id === ROOT_COMPANY_ID.MSD) {
        this.defaultStatusKey = null
      } else {
        this.defaultStatusKey = 'Disqualified'
      }
      this.$refs.changeStatusDialog.showDialog()
    },
    handleChangeStatus (status) {
      this.defaultStatusKey = status.key
      this.$refs.changeStatusDialog.showDialog()
    },
    handleChangeStatusSuccess (status, changeInfo) {
      this.candidateInfo.current_status = addI18nProperty(status, 'i18nName', 'name_cn', 'name_en')

      if (status.key === 'Hired') {
        this.candidateInfo.onboard_date = changeInfo.onboardDate
      } else if (['6cc1127e-62d2-11ec-930c-00163e0fbcc2', 'd71f06b8-4a32-11ef-94be-00163e0fbcc2'].includes(status.key)) {
        // 6cc1127e-62d2-11ec-930c-00163e0fbcc2 是 Ready to Hire
        // d71f06b8-4a32-11ef-94be-00163e0fbcc2 是 Ready for Hire
        this.candidateInfo.hired_date = changeInfo.onboardDate
      }
      this.updateApplicationStatus()
    },
    handleBreadCrumbNavigationClick () {
      const query = this.$route.query
      let url = `/v-admin/job/${query.job_id}/application`
      if (query.back) {
        url = query.back
      }
      this.$router.push(url)
    },
    async handlePrevBtnClick () {
      if (this.$refs.collapseInfo && !this.$refs.collapseInfo.checkRouteCanLeave()) {
        return
      }
      if (this.applicationIdListLoading || this.applicationChanging) {
        return
      }
      const info = this.applicationIdList[this.currentApplicationIdIndex - 1]
      if (!info) {
        return
      }
      const query = {
        ...this.$route.query,
        candidate_id: info.candidate_id,
        job_id: info.job_id
      }
      this.applicationChanging = true
      try {
        await this.$router.push({ query })
      } catch (error) {
        this.applicationChanging = false
        throw error
      }
    },
    async handleNextBtnClick () {
      if (this.$refs.collapseInfo && !this.$refs.collapseInfo.checkRouteCanLeave()) {
        return
      }
      if (this.applicationIdListLoading || this.applicationChanging) {
        return
      }
      const info = this.applicationIdList[this.currentApplicationIdIndex + 1]
      if (!info) {
        return
      }
      const query = {
        ...this.$route.query,
        candidate_id: info.candidate_id,
        job_id: info.job_id
      }
      this.applicationChanging = true
      try {
        await this.$router.push({ query })
      } catch (error) {
        this.applicationChanging = false
        throw error
      }
    },
    async handleCreateAIResumeMatchBtnClick () {
      const aiTask = {
        task: null,
        isFinished: false
      }
      let aiMatchInfo = {}
      this.aiResumeMatchLoading = true
      aiTask.task = new AsyncTask(() => this.createAIResumeMatch({
        candidate_ids: [this.candidateId],
        job_id: this.jobId,
        await_result: true
      }), (taskId) => this.getAIMatchInfoByTaskId(taskId), (taskId, resp) => {
        if (resp) {
          this.aiResumeMatchLoading = false
          aiTask.isFinished = true
          const result = resp.result
          const key = `${this.jobId}_${this.candidateId}`
          aiMatchInfo = _.get(result, key)
          this.candidateInfo.resume_ai_match_score = aiMatchInfo.resume_ai_match_score
          this.candidateInfo.resume_ai_match_summary_cn = aiMatchInfo.resume_ai_match_summary_cn
          this.candidateInfo.resume_ai_match_summary_en = aiMatchInfo.resume_ai_match_summary_en
          this.candidateInfo.has_ai_match_record = true
          this.initTabScore()
        }
      })
    },
    updateHrQuestion () {
      this.hasHrQuestion = true
    },
    handleOpen (collapseWidth) {
      if (!this.showCollapse) {
        this.showCollapse = true
        this.fixedNavigationRight += collapseWidth
      }
    },
    handleClose (collapseWidth) {
      if (this.showCollapse) {
        this.showCollapse = false
        this.fixedNavigationRight -= collapseWidth
      }
    }
  },
  watch: {
    pageType () {
      this.refreshActiveTab()
    },
    profileTabRequestKey () {
      this.handleApplicationChange()
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-info-page {
  padding: 6px 0 20px;
  display: flex;
  height: 100%;

  .profile-info {
    flex: 1;
    display: flex;
  }

  &.collapse {
    padding-bottom: 0;

    .profile-info {
      margin-left: 12px;
      overflow: auto;

      ::v-deep {
        .tab-list {
          .tab-item {

          }
        }
      }
    }

    .collapse-container {
      margin-left: 12px;
    }
  }

  .profile-info-container {
    display: flex;
    flex-direction: column;
    width: 95%;
    min-width: min-content;
    margin: 0 auto;
    padding-top: 26px;
    position: relative;

    .breadcrumb-navigation {
      line-height: 20px;
      color: #606266;
      position: absolute;
      top: 0;
      left: 0;

      .split {
        margin: 0 10px;
      }

      .navigation {
        cursor: pointer;
        color: #0096cc;

        &.active {
          cursor: default;
          color: #606266;
        }
      }
    }

    .application-list-navigation {
      position: absolute;
      top: 0;
      right: 0;

      .navigation-btn {
        padding: 0 3px;
        line-height: 17px;
        border-radius: 2px;
        border-color: #c8c8c8;

        .btn-icon {
          font-size: 12px;
          line-height: inherit;
          vertical-align: initial;
        }
      }

      .position  {
        margin: 0 34px;
        line-height: 20px;
        color: #606266;

        .split {
          margin: 0 10px;
        }
      }

      .loading {
        position: absolute;
        left: 0;
        top: 0;

        ::v-deep .spinner {
          height: 30px;
        }
      }
    }

    .base-info-container {
      padding: 18px 40px;
      border-radius: 6px;
      background: #fff;
    }

    .extra {
      padding-top: 35px;
    }

    .detail-info-container {
      flex: 1;
      margin-top: 14px;
      padding: 12px 24px 38px;
      border-radius: 6px;
      background: #fff;

      .info-tab-card {
        min-height: 100%;

        .score-container {
          position: absolute;
          right: -2px;
          top: 0;
          transform: translate(0, -50%);

          .score-item {
            display: inline-block;
            width: 30px;
            height: 30px;
            line-height: 26px;
            border: 2px solid #0096cc;
            border-radius: 50%;
            background-color: #fff;
            color: #0096cc;
            text-align: center;
            font-weight: bold;
            font-size: 14px;

            &.decimal-score-item {
              font-size: 12px;
            }

            & + .score-item{
              margin-left: 5px;
            }
          }

          .chart-score {
            display: inline-block;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background-color: #fff;

            & + .chart-score {
              margin-left: 5px;
            }
          }
        }
        .offer-status {
          position: absolute;
          top: 6px;
          right: 26%;
          width: 8px;
          height: 8px;
          background: var(--offer-status-color);
          border: 1px solid var(--offer-status-color);
          border-radius: 100%;
        }
      }
    }

    .fixed-list-navigation {
      position: fixed;
      bottom: 48px;
      right: 124px;
      transition: right 0.5s ease-in-out;

      .navigation-btn {
        width: 40px;
        height: 40px;
        padding: 0 8px;
        border-radius: 50%;
        border: 0;
        box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.06), 0px 3px 6px 0px rgba(0,0,1,0.12);
        text-align: center;
        background: #ffffff;

        & + .navigation-btn {
          margin-left: 38px;
        }

        &:not(.is-disabled):hover {
          background: #0096cc;

          .btn-icon {
            color: #ffffff;
          }
        }

        &.prev-btn {
          .btn-icon {
            transform: rotateZ(180deg);
          }
        }

        .btn-icon {
          display: inline-block;
          font-size: 15px;
          color: #606266;

          &::before {
            padding-left: 2px;
          }
        }
      }
    }
  }
}
.applied-jobs-tooltip {
  .applied-jobs-tooltip-content {
    width: 240px;
    max-height: 240px;
    overflow-y: auto;
    color: #303133;
    line-height: 20px;

    .tooltip-line {
      white-space: normal;
      word-break: break-word;
    }

    .section-title {
      margin-top: 8px;
    }

    .tooltip-job-item {
      display: flex;
      align-items: flex-start;
      margin-top: 4px;
      white-space: normal;

      .dot {
        flex-shrink: 0;
        width: 1em;
        margin-right: 4px;
        font: inherit;
        line-height: inherit;
        text-align: center;
        color: black;

        &::before {
          content: '\2022';
        }
      }
    }
  }
}
</style>
